import React from 'react';

import { usePutGroup } from '@vlabs/api/artemy/client';
import type { GroupRecord, PermissionTag, PutGroupRequest } from '@vlabs/api/artemy/model';
import { CrossCircleIcon } from '@vlabs/icons';
import { useModal, Tooltip } from '@vlabs/uikit';
import type { IconButtonProps } from '@vlabs/uikit/components/controls/button/icon-button/IconButton.js';
import { IconButton } from '@vlabs/uikit/components/controls/button/icon-button/IconButton.js';
import { toast } from 'react-toastify';

import { buildTestid } from '@vlabs/monokit/shared/lib/utils';

import { DetachPermissionTagGroup } from './DetachPermissionTagGroup';
import type { DetachPermissionTagGroupSelection } from './DetachPermissionTagGroup';
import { useDetachPermissionTagGroupTranslation } from './i18n';
import type { DetachPermissionTagGroup as DetachPermissionTagGroupData } from './types';

const displayName = 'DetachPermissionTagGroupButton';
const testid = buildTestid(displayName);

type DetachPermissionTagGroupButtonProps = IconButtonProps & DetachPermissionTagGroupData;

function toPutGroupRequest(group: GroupRecord, permissionTag: PermissionTag): PutGroupRequest {
  return {
    analytics: group.analytics,
    description: group.description,
    group_name: group.group_name,
    owner_department: group.owner_department,
    permission_tag: permissionTag,
    stream_settings: group.stream_settings,
  };
}

function DetachPermissionTagGroupButton({
  groupId,
  disabled,
  title,
  'data-testid': dataTestId = undefined,
  ...props
}: DetachPermissionTagGroupButtonProps): React.JSX.Element {
  const { t } = useDetachPermissionTagGroupTranslation();
  const modal = useModal();
  const putGroup = usePutGroup({
    mutation: {
      onSuccess: () => {
        toast.success(t('on_success'));
        modal.close();
      },
      onError: () => toast.error(t('on_error')),
    },
  });

  const handleSubmit = ({ group, permissionTag }: DetachPermissionTagGroupSelection): void => {
    putGroup.mutate({
      groupId: group.group_id,
      data: toPutGroupRequest(group, permissionTag),
    });
  };

  const tooltipContent = disabled ? title : undefined;
  return (
    <React.Fragment>
      {modal.wrap(
        <DetachPermissionTagGroup
          groupId={groupId}
          isPending={putGroup.isPending}
          onCancel={modal.close}
          onSubmit={handleSubmit}
        />,
      )}
      <Tooltip
        position="bottom"
        tooltipContent={tooltipContent}
      >
        <IconButton
          disabled={disabled}
          icon={<CrossCircleIcon />}
          kind="negative"
          onClick={modal.open}
          variant="dimmed"
          {...testid(dataTestId)}
          {...props}
        />
      </Tooltip>
    </React.Fragment>
  );
}

DetachPermissionTagGroupButton.displayName = displayName;

export { DetachPermissionTagGroupButton };
